import React from 'react';
const { useEffect, useRef, useState } = React;
import { inputSt, labelSt, mutedSt } from '../../diagnostic/shared.jsx';
import {
  BRAND_TEXT_LIMITS as BL, SOCIAL_CASTS, SOCIAL_GUESTS_MAX, SOCIAL_ALIGNS, SOCIAL_SIDES, HALO_COLORS, SOCIAL_STYLE_DEFAULTS,
  SOCIAL_HIGHLIGHTS_MAX, SELF_FRAME_DEFAULT, SELF_ZOOM, GUEST_ZOOM, activePeople, highlightKey,
} from '../../../brandConfig.js';
import { chipSt, Notice } from '../shared.jsx';
import { Thumb, PhotoUpload, photoStatus } from './BrandPhotos.jsx';
import { loadPhoto } from './brandClient.js';

// The collection "YouTube y LinkedIn" in the piece editor: who appears (Ale,
// guests or both), each person's photo with its own crop, name and role, and
// the style choices (alignment, side, highlighted words, halo, details).
// Photos are only cropped, moved and scaled with one scale: never deformed or
// retouched. Ale's photo keeps the waist-up framing.

const Range = ({ label, value, min, max, step, onChange, text }) => (
  <label style={{ display: 'grid', gridTemplateColumns: '130px 1fr 52px', gap: 6, alignItems: 'center', fontSize: 12 }}>
    <span>{label}</span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} aria-valuetext={text} />
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{text}</span>
  </label>
);

/** Ale's photo with the head and waist lines she sets (only a guide: the photo is not changed). */
function FrameGuide({ photoId, frame }) {
  const ref = useRef(null);
  const [img, setImg] = useState(null);
  useEffect(() => { let on = true; loadPhoto(photoId).then(x => on && setImg(x)).catch(() => {}); return () => { on = false; }; }, [photoId]);
  useEffect(() => {
    const c = ref.current;
    if (!c || !img) return;
    const w = 120, h = Math.round(120 * img.h / img.w);
    c.width = w * 2; c.height = h * 2; c.style.width = `${w}px`; c.style.height = `${h}px`;
    const x = c.getContext('2d');
    x.fillStyle = '#E8E4DC'; x.fillRect(0, 0, c.width, c.height);
    x.drawImage(img.img, 0, 0, c.width, c.height);
    const line = (v, color) => { x.strokeStyle = color; x.lineWidth = 4; x.setLineDash([10, 6]); x.beginPath(); x.moveTo(0, v * c.height); x.lineTo(c.width, v * c.height); x.stroke(); };
    line(frame.head, '#2D5F99'); line(frame.waist, '#B4362C');
  }, [img, frame.head, frame.waist]);
  return <canvas ref={ref} role="img" aria-label="Tu foto con la línea de la cabeza (azul) y la de la cintura (roja)" style={{ borderRadius: 6, display: 'block', background: '#eee' }} />;
}

function PersonEditor({ def, piece, photos, setPiece, onPhotosChanged }) {
  const cur = piece.photos?.[def.key] || null;
  const info = piece.content.people?.[def.key] || {};
  const p = cur?.photoId ? photos.find(x => x.id === cur.photoId) : null;
  const [upload, setUpload] = useState(false);
  const choices = photos.filter(x => !x.archived && x.hasImage !== false && (def.self ? x.kind === 'self' : x.kind !== 'self'));
  const setPh = (v) => setPiece(pc => ({ ...pc, photos: { ...pc.photos, [def.key]: v || undefined } }));
  const setInfo = (patch) => setPiece(pc => ({ ...pc, content: { ...pc.content, people: { ...pc.content.people, [def.key]: { ...pc.content.people?.[def.key], ...patch } } } }));
  const choose = (photo) => {
    setPh(def.self
      ? { photoId: photo.id, crop: { x: 0.5, y: 0.5, zoom: 1 }, frame: cur?.frame || { ...SELF_FRAME_DEFAULT } }
      : { photoId: photo.id, crop: { x: 0.5, y: 0.4, zoom: 1 } });
    if (!def.self && !info.name && photo.personName) setInfo({ name: photo.personName, role: info.role || photo.personRole || '' });
  };
  const crop = { x: 0.5, y: 0.5, zoom: 1, ...(cur?.crop || {}) };
  const frame = { ...SELF_FRAME_DEFAULT, ...(cur?.frame || {}) };
  const setCrop = (k, v) => setPh({ ...cur, crop: { ...crop, [k]: v } });
  const setFrame = (k, v) => setPh({ ...cur, frame: { ...frame, [k]: v } });
  const pct = (v) => `${Math.round(v * 100)}%`;
  return (
    <fieldset style={{ border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, margin: '0 0 10px' }}>
      <legend style={{ fontWeight: 700, fontSize: 13, padding: '0 4px' }}>{def.label}</legend>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {choices.map(x => {
          const st = photoStatus(x);
          return (
            <button key={x.id} type="button" aria-pressed={cur?.photoId === x.id} onClick={() => choose(x)}
              style={{ border: `2px solid ${cur?.photoId === x.id ? 'var(--globant-green)' : 'transparent'}`, borderRadius: 10, padding: 2, background: 'none', cursor: 'pointer', textAlign: 'center', width: 84 }}>
              <Thumb photo={x} size={74} alt="" />
              <span style={{ display: 'block', fontSize: 11, overflowWrap: 'anywhere' }}>{x.kind === 'self' ? (x.name || 'Mía') : x.personName || x.name}</span>
              {!st.ok && <span style={{ display: 'block', fontSize: 10, color: '#9A6212' }}>{st.text}</span>}
            </button>
          );
        })}
        {!choices.length && <span style={mutedSt}>{def.self ? 'Todavía no hay fotos tuyas.' : 'Todavía no hay fotos de invitados.'} Cargala aquí abajo.</span>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        <button type="button" className="action-btn" style={{ fontSize: 12 }} onClick={() => setUpload(u => !u)}>{upload ? 'Cerrar' : cur?.photoId ? 'Reemplazar con una foto nueva' : 'Cargar una foto'}</button>
        {cur?.photoId && <button type="button" className="action-btn" style={{ fontSize: 12 }} onClick={() => setPh(null)}>Quitar la foto</button>}
      </div>
      {upload && (
        <PhotoUpload compact defaultKind={def.self ? 'self' : 'guest'} onUploaded={(photo) => { setUpload(false); onPhotosChanged?.(); choose(photo); }} />
      )}
      {cur?.photoId && (
        <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
          {p && <div style={{ fontSize: 12 }}>{p.kind === 'self' ? 'Foto tuya' : `${p.personName || 'Sin nombre'}${p.personRole ? ` · ${p.personRole}` : ''}`} · <span style={{ color: photoStatus(p).ok ? '#1E7B4F' : '#9A6212' }}>{photoStatus(p).text}</span></div>}
          {def.self ? (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <FrameGuide photoId={cur.photoId} frame={frame} />
              <div style={{ flex: '1 1 240px', display: 'grid', gap: 4 }}>
                <div style={mutedSt}>De la cintura para arriba: tu cintura queda en el borde inferior de la foto y la cabeza con aire arriba. Si tu foto muestra más que eso, subí la línea roja hasta la cintura.</div>
                <Range label="Línea de la cabeza" value={frame.head} min={0} max={0.6} step={0.01} onChange={v => setFrame('head', Math.min(v, frame.waist - 0.15))} text={pct(frame.head)} />
                <Range label="Línea de la cintura" value={frame.waist} min={0.3} max={1} step={0.01} onChange={v => setFrame('waist', Math.max(v, frame.head + 0.15))} text={pct(frame.waist)} />
                <Range label="Mover a los lados" value={crop.x} min={0} max={1} step={0.01} onChange={v => setCrop('x', v)} text={pct(crop.x)} />
                <Range label="Tamaño" value={crop.zoom} min={SELF_ZOOM.min} max={SELF_ZOOM.max} step={0.01} onChange={v => setCrop('zoom', v)} text={`${crop.zoom.toFixed(2)}×`} />
                <div><button type="button" className="action-btn" style={{ fontSize: 12 }} onClick={() => setPh({ ...cur, crop: { x: 0.5, y: 0.5, zoom: 1 } })}>Centrar</button></div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 4 }}>
              <Range label="Mover a los lados" value={crop.x} min={0} max={1} step={0.01} onChange={v => setCrop('x', v)} text={pct(crop.x)} />
              <Range label="Mover arriba o abajo" value={crop.y} min={0} max={1} step={0.01} onChange={v => setCrop('y', v)} text={pct(crop.y)} />
              <Range label="Ampliar" value={crop.zoom} min={GUEST_ZOOM.min} max={GUEST_ZOOM.max} step={0.05} onChange={v => setCrop('zoom', v)} text={`${crop.zoom.toFixed(2)}×`} />
              <div><button type="button" className="action-btn" style={{ fontSize: 12 }} onClick={() => setPh({ ...cur, crop: { x: 0.5, y: 0.4, zoom: 1 } })}>Centrar</button></div>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'flex-end' }}>
        <label style={{ flex: '1 1 160px' }}><span style={labelSt}>Nombre</span><input style={inputSt} value={info.name ?? ''} maxLength={BL.personName} placeholder={def.self ? 'Alejandra Cadario' : p?.personName || ''} onChange={e => setInfo({ name: e.target.value })} /></label>
        <label style={{ flex: '1 1 160px' }}><span style={labelSt}>Rol</span><input style={inputSt} value={info.role ?? ''} maxLength={BL.personRole} placeholder={def.self ? 'Anfitriona' : p?.personRole || ''} onChange={e => setInfo({ role: e.target.value })} /></label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, paddingBottom: 8 }}>
          <input type="checkbox" checked={info.show ?? piece.template === 'podcast'} onChange={e => setInfo({ show: e.target.checked })} /> Mostrar nombre y rol
        </label>
      </div>
    </fieldset>
  );
}

/** Who appears and each person's photo. */
export function SocialPeople({ piece, tpl, photos, setPiece, onPhotosChanged }) {
  const c = piece.content;
  const cast = c.cast || 'self';
  const setContent = (patch) => setPiece(pc => ({ ...pc, content: { ...pc.content, ...patch } }));
  const shown = activePeople(c);
  return (
    <div>
      <Notice icon="📷" tone="info">Cada foto se carga, reemplaza, mueve y amplía por separado, siempre con una sola escala: no se deforma ni se retoca. Un PNG con fondo transparente (silueta recortada) se usa tal cual, con el halo opcional alrededor. La composición se adapta a la cantidad de personas, sin textos sobre los rostros.</Notice>
      <span style={labelSt}>Quién aparece</span>
      <div role="radiogroup" aria-label="Quién aparece" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {SOCIAL_CASTS.map(x => <button key={x.key} type="button" role="radio" aria-checked={cast === x.key} style={chipSt(cast === x.key)} onClick={() => setContent({ cast: x.key })}>{x.label}</button>)}
      </div>
      {cast !== 'self' && (
        <>
          <span style={labelSt}>Cantidad de invitados</span>
          <div role="radiogroup" aria-label="Cantidad de invitados" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {Array.from({ length: SOCIAL_GUESTS_MAX }, (_, i) => i + 1).map(n => <button key={n} type="button" role="radio" aria-checked={(c.guests || 1) === n} style={chipSt((c.guests || 1) === n)} onClick={() => setContent({ guests: n })}>{n}</button>)}
          </div>
        </>
      )}
      {tpl.photos.filter(d => shown.includes(d.key)).map(d => <PersonEditor key={d.key} def={d} piece={piece} photos={photos} setPiece={setPiece} onPhotosChanged={onPhotosChanged} />)}
      <div style={mutedSt}>Las fotos de quienes no aparecen quedan guardadas en la pieza por si vuelves a mostrarlos. La foto de otra persona necesita su nombre y su autorización para aprobar o exportar.</div>
    </div>
  );
}

/** Alignment, side of the photos, highlighted words, halo and details. */
export function SocialStyle({ piece, tpl, setPiece }) {
  const st = { ...SOCIAL_STYLE_DEFAULTS, ...(piece.content.style || {}) };
  const set = (patch) => setPiece(pc => ({ ...pc, content: { ...pc.content, style: { ...SOCIAL_STYLE_DEFAULTS, ...pc.content.style, ...patch } } }));
  const words = String(piece.content.slots?.headline?.text || '').split(/\s+/).filter(Boolean);
  const keys = [...new Set(words.map(highlightKey).filter(Boolean))];
  const on = new Set(st.highlights || []);
  const toggle = (k) => set({ highlights: on.has(k) ? st.highlights.filter(x => x !== k) : [...(st.highlights || []), k].slice(0, SOCIAL_HIGHLIGHTS_MAX) });
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div>
        <span style={labelSt}>Palabras destacadas (en mostaza)</span>
        {!keys.length ? <div style={mutedSt}>Escribe el título para elegirlas.</div> : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {words.map((w, i) => { const k = highlightKey(w); if (!k) return null; return <button key={`${i}-${w}`} type="button" aria-pressed={on.has(k)} style={chipSt(on.has(k))} onClick={() => toggle(k)}>{w}</button>; })}
          </div>
        )}
        <div style={mutedSt}>Hasta {SOCIAL_HIGHLIGHTS_MAX}. Una palabra elegida se destaca cada vez que aparece en el título.{tpl.label === 'Palabras destacadas' ? ' En esta plantilla van en bloques mostaza; el resto, en bloques marfil.' : ''}</div>
      </div>
      <div>
        <span style={labelSt}>Alineación del texto</span>
        <div role="radiogroup" aria-label="Alineación del texto" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {SOCIAL_ALIGNS.map(x => <button key={x.key} type="button" role="radio" aria-checked={st.align === x.key} style={chipSt(st.align === x.key)} onClick={() => set({ align: x.key })}>{x.label}</button>)}
        </div>
      </div>
      <div>
        <span style={labelSt}>Lado de las fotos</span>
        <div role="radiogroup" aria-label="Lado de las fotos" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {SOCIAL_SIDES.map(x => <button key={x.key} type="button" role="radio" aria-checked={st.side === x.key} style={chipSt(st.side === x.key)} onClick={() => set({ side: x.key })}>{x.label}</button>)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}><input type="checkbox" checked={st.halo} onChange={e => set({ halo: e.target.checked })} /> Borde o halo alrededor de las fotos</label>
        {st.halo && (
          <span role="radiogroup" aria-label="Color del halo" style={{ display: 'flex', gap: 6 }}>
            {HALO_COLORS.map(x => <button key={x.key} type="button" role="radio" aria-checked={st.haloColor === x.key} style={chipSt(st.haloColor === x.key)} onClick={() => set({ haloColor: x.key })}>{x.label}</button>)}
          </span>
        )}
      </div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}><input type="checkbox" checked={st.decor} onChange={e => set({ decor: e.target.checked })} /> Detalles gráficos ({String(tpl.decor || '').toLowerCase()})</label>
    </div>
  );
}
